import React, { ReactNode, createContext, useContext, useMemo } from 'react';

import { useAuth, useUser } from '@clerk/clerk-react';

/**
 * Provider-agnostic auth surface consumed by UserContext, RootLayout, etc.
 * Clerk is the sole auth backend (see
 * docs/implementation_plans/users-management/06-decommission-cognito.md).
 */
export interface AppAuthValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Raw external subject id (Clerk user id) -- NOT the CortexUser DB id. */
  userId: string | null;
  userEmail: string | null;
  signOut: () => Promise<void>;
}

const AppAuthContext = createContext<AppAuthValue | undefined>(undefined);

export function useAppAuth(): AppAuthValue {
  const ctx = useContext(AppAuthContext);
  if (ctx === undefined) {
    throw new Error('useAppAuth must be used within a ClerkAppAuthProvider');
  }
  return ctx;
}

/** Set by `docker_run.sh --devbox`; see docs/local-dev/devbox-over-tailscale.md. */
export function isDevboxAuth(): boolean {
  return process.env.REACT_APP_DEVBOX_AUTH === '1';
}

/** Signed-in without Clerk: the backend resolves the user from the device's Tailscale address. */
export function DevboxAppAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AppAuthValue>(
    () => ({
      isLoading: false,
      isAuthenticated: true,
      // A stable id, because Notifications uses it as its status-message cursor.
      userId: `devbox:${process.env.REACT_APP_DEVBOX_IDENTITY || 'unknown'}`,
      userEmail: process.env.REACT_APP_DEVBOX_IDENTITY || null,
      // There is no session to end, so sign-out only reloads.
      signOut: async () => {
        window.location.reload();
      },
    }),
    []
  );

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}

export function ClerkAppAuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut: clerkSignOut } = useAuth();

  const value = useMemo<AppAuthValue>(
    () => ({
      isLoading: !isLoaded,
      isAuthenticated: !!isSignedIn,
      userId: user?.id ?? null,
      userEmail: user?.primaryEmailAddress?.emailAddress ?? null,
      signOut: async () => {
        await clerkSignOut();
      },
    }),
    [isLoaded, isSignedIn, user, clerkSignOut]
  );

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
}
